import React, {useEffect, useState} from "react";
import {Button, Form, Modal} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExclamationCircle, faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import ModalControls from "../../common/ModalControls";
import {useLocation, useNavigate} from "react-router-dom";
import {defaultFormState} from "../../../../defaults/Forms";
import {
  compareData,
  onCloseDetails,
  onItemsListDeleteButtonClick,
  onSaveAndClose,
  restoreFormData
} from "../../../../helpers/ComponentHelper";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import DeleteWarningModal from "../../common/DeleteWarningModal";
import TextInput from "../../../elements/TextInput";
import TextArea from "../../../elements/TextArea";
import useCrud from "../../../../hooks/useCrud";
import {Table} from "../../../table/Table";
import "../../../../css/DictionaryForms.css";

const defaultItem = {
  id: "",
  name: "",
  shortName: "",
  employmentPercentage: 100,
  workingHours: 8,
  description: ""
};

const WorkingTime = () => {
  const dataUrl = "/working-times";
  const itemName = "wymiar czasu pracy";
  const navigate = useNavigate();
  const location = useLocation();
  const {createItem, updateItem, deleteItem, getItems} = useCrud(dataUrl);

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [updateConflict, setUpdateConflict] = useState(false);

  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false, visible: false},
    {label: "Nazwa", accessor: "name", sortable: true, searchable: true},
    {label: "Skrót", accessor: "shortName", sortable: true, searchable: true},
    {
      label: "Wymiar etatu",
      accessor: "employmentPercentage",
      sortable: true,
      searchable: true,
      format: value => value === null || value === undefined ? "" : `${Number(value).toLocaleString("pl-PL", {maximumFractionDigits: 2})}%`
    },
    {
      label: "Norma dobowa",
      accessor: "workingHours",
      sortable: true,
      searchable: false,
      format: value => value === null || value === undefined ? "" : `${Number(value).toLocaleString("pl-PL", {maximumFractionDigits: 2})} h`
    },
    {label: "Opis", accessor: "description", sortable: true, searchable: true},
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    {label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false}
  ];

  const state = {
    itemsList,
    setItems,
    currentItem,
    setCurrentItem,
    currentFormState,
    setCurrentFormState,
    defaultItem,
    backupItem,
    setBackupItem,
    itemChanged,
    setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  };

  const loadData = async () => {
    setLoading(true);
    const response = await getItems(dataUrl);
    if (response.status === 200) {
      setItems(response.data || []);
    } else if (response.status === 401 || response.status === 403) {
      navigate("/login", {state: {from: location}, replace: true});
    } else {
      alert("Nie udało się pobrać wymiarów czasu pracy.");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [location.key]);

  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
  }, [itemChanged]);

  useEffect(() => {
    if (allowDelete !== null) {
      onItemsListDeleteButtonClick(currentFormState, setCurrentFormState, itemName, allowDelete, currentItem.name);
    }
  }, [allowDelete]);

  const onDelete = async () => {
    const response = await deleteItem(`${dataUrl}/${currentItem.id}`, currentItem.id, state);
    if (response === 401 || response === 403) {
      navigate("/login", {state: {from: location}, replace: true});
      return;
    }
    if (response) await loadData();
  };

  const onSaveItemClick = async event => {
    event.preventDefault();
    setUpdateConflict(false);

    const percentage = Number(currentItem.employmentPercentage);
    const validPercentage = Number.isFinite(percentage) && percentage > 0 && percentage <= 100;
    if (!currentItem.name?.trim() || !currentItem.shortName?.trim() || !validPercentage) {
      if (!currentItem.name?.trim()) document.getElementById("name")?.classList.add("form-input-invalid");
      if (!currentItem.shortName?.trim()) document.getElementById("shortName")?.classList.add("form-input-invalid");
      if (!validPercentage) document.getElementById("employmentPercentage")?.classList.add("form-input-invalid");
      return;
    }

    const item = {
      id: currentItem.id || null,
      name: currentItem.name.trim(),
      shortName: currentItem.shortName.trim(),
      employmentPercentage: percentage,
      workingHours: Math.round((8 * percentage / 100) * 100) / 100,
      description: currentItem.description?.trim() || null
    };

    const response = currentFormState.formAddingDataMode
      ? await createItem(dataUrl, {...item, id: undefined}, state)
      : await updateItem(`${dataUrl}/${item.id}`, item, state);

    if (response?.status === 401 || response?.status === 403) {
      navigate("/login", {state: {from: location}, replace: true});
    } else if (response?.status === 409) {
      setUpdateConflict(true);
    } else if (response && response.status >= 200 && response.status < 300) {
      onSaveAndClose({state});
      await loadData();
    }
  };

  const getCompleteItem = async id => {
    const response = await getItems(`${dataUrl}/${id}`);
    if (response.status === 200) return response.data;
    if (response.status === 401 || response.status === 403) {
      navigate("/login", {state: {from: location}, replace: true});
      return null;
    }
    alert("Nie udało się pobrać wymiaru czasu pracy.");
    return null;
  };

  const onClose = () => {
    setUpdateConflict(false);
    onCloseDetails({state});
  };

  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem,
    currentFormState,
    setCurrentFormState,
    formDescription: "",
    formHeader: "Nowy wymiar czasu pracy",
    buttonTitle: "Dodaj wymiar etatu"
  };

  let dataSectionContent;
  if (loading) {
    dataSectionContent = <LoadingDataDiv/>;
  } else if (itemsList.length > 0) {
    dataSectionContent = (
      <Table
        rows={itemsList}
        columns={columns}
        state={state}
        checkRelatedItems={() => setAllowDelete(true)}
        formHeader={`Edycja ${itemName}`}
        relatedItemsUrl={null}
        getCompleteItem={getCompleteItem}
      />
    );
  } else {
    dataSectionContent = (
      <div className="warn_info">
        <FontAwesomeIcon className="warning-icon div-visible" icon={faExclamationCircle}/>
        &nbsp;&nbsp;&nbsp;BRAK ZDEFINIOWANYCH WYMIARÓW CZASU PRACY
      </div>
    );
  }

  const percentage = Number(currentItem.employmentPercentage);
  const dailyHours = Number.isFinite(percentage) ? 8 * percentage / 100 : 0;
  const modalTitle = currentFormState.formAddingDataMode
    ? "Nowa pozycja: Wymiar czasu pracy"
    : `Edycja: ${currentItem.name || "Wymiar czasu pracy"}`;

  return (
    <div id="layoutSidenav_content" className="dictionary-page">
      <AppComponentCardHeader title="Pozycje słownika Wymiary czasu pracy" buttonProps={addDataButtonProps}/>

      <div className="container-fluid px-4 py-3">
        <div className="small text-muted mb-3">
          Wymiar etatu jest przypisywany do umowy o pracę. Na jego podstawie system wylicza miesięczną normę czasu pracy oraz godzinowy koszt wynagrodzenia miesięcznego.
        </div>
        <div className={itemsList.length > 0 ? "card shadow p-1 mb-5 bg-white rounded-2" : "card shadow mb-5 bg-transparent rounded-2"}>
          {dataSectionContent}
        </div>
      </div>

      <Modal
        show={currentFormState.showForm}
        onHide={onClose}
        backdrop="static"
        keyboard={false}
        className="dictionary-form-modal"
        dialogClassName="dictionary-form-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="dictionary-form-intro">
            <div>
              <strong>{currentFormState.formAddingDataMode ? "Dodajesz wymiar etatu" : "Edytujesz wymiar etatu"}</strong>
              <span>Przykładowo 100% oznacza pełny etat, 50% oznacza pół etatu.</span>
            </div>
            {!currentFormState.formAddingDataMode && (
              <Button size="sm" variant="outline-secondary" onClick={() => restoreFormData({state})}>
                Przywróć wartości
              </Button>
            )}
          </div>

          <form className="dictionary-form-grid">
            <div className="dictionary-form-field">
              <label htmlFor="name"><span className="required">*</span> Nazwa</label>
              <TextInput propertyName="name" required={true} state={state}/>
              <small>Przykład: Pełny etat.</small>
            </div>

            <div className="dictionary-form-field dictionary-form-field-short">
              <label htmlFor="shortName"><span className="required">*</span> Skrót</label>
              <TextInput propertyName="shortName" maxlen="20" required={true} state={state}/>
              <small>Przykład: 1/1 albo 1/2.</small>
            </div>

            <div className="dictionary-form-field">
              <label htmlFor="employmentPercentage"><span className="required">*</span> Wymiar etatu (%)</label>
              <Form.Control
                id="employmentPercentage"
                type="number"
                min="0.01"
                max="100"
                step="0.01"
                value={currentItem.employmentPercentage ?? ""}
                onChange={event => {
                  setItemChanged(current => !current);
                  setCurrentItem(current => ({...current, employmentPercentage: event.target.value}));
                  setCurrentFormState(current => ({...current, formSaveButtonDisabled: false}));
                }}
              />
              <small>Zakres od 0,01% do 100%.</small>
            </div>

            <div className="dictionary-form-field dictionary-form-field-short">
              <label>Norma dobowa</label>
              <Form.Control value={`${dailyHours.toLocaleString("pl-PL", {maximumFractionDigits: 2})} h`} disabled readOnly/>
              <small>Wyliczana z bazowej normy 8 h dla 100%.</small>
            </div>

            <div className="dictionary-form-field dictionary-form-field-full">
              <label htmlFor="description">Opis</label>
              <TextArea propertyName="description" required={false} rows="3" state={state}/>
            </div>
          </form>

          {updateConflict && (
            <div className="dictionary-form-error">
              <FontAwesomeIcon icon={faInfoCircle}/>
              <span>Istnieje już wymiar czasu pracy o tej nazwie, skrócie albo wartości procentowej.</span>
            </div>
          )}
        </Modal.Body>

        <ModalControls
          onDelete={onDelete}
          onCloseDetails={onClose}
          onSubmit={onSaveItemClick}
          state={state}
        />
      </Modal>

      <DeleteWarningModal state={state} onDelete={onDelete} deleteItemName={itemName}/>
    </div>
  );
};

export default WorkingTime;
